import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, IconButton, Grid } from '@mui/material';
import {
  ArrowBack,
  VideoLibrary,
  Settings,
  Storage,
} from '@mui/icons-material';
import {
  Container,
  Card,
  CardHeader,
  CardContent,
  Stack,
} from '../design-system';
import { colors, spacing, typography } from '../design-system';
import { useTranslation } from 'react-i18next';
import VideoEventTracker from '../components/VideoEventTracker';
import usePageTitle from '../hooks/usePageTitle';

/**
 * HerramientasPageModern - Modern tools page
 * Utility tools and configuration options
 */
const HerramientasPageModern = () => {
  usePageTitle('Herramientas');
  const { t } = useTranslation('tools');
  const navigate = useNavigate();

  const cleanFiltersAndBack = () => {
    navigate(-1);
  };

  const tools = [
    {
      title: t('tools.videoTracking.title'),
      description: t('tools.videoTracking.description'),
      icon: VideoLibrary,
      color: colors.primary[500],
      available: true,
    },
    {
      title: t('tools.settings.title'),
      description: t('tools.settings.description'),
      icon: Settings,
      color: colors.info[500],
      available: false,
    },
    {
      title: t('tools.dataManagement.title'),
      description: t('tools.dataManagement.description'),
      icon: Storage,
      color: colors.success[500],
      available: false,
    },
  ];

  return (
    <Box className="wp-page" sx={{ minHeight: '100vh', backgroundColor: 'background.default' }}>
      {/* Header */}
      <Box
        className="wp-heading"
        sx={{
          bgcolor: 'background.paper',
          borderBottom: 1, borderColor: 'divider',
          position: 'sticky',
          top: 0,
          zIndex: 10,
        }}
      >
        <Box sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton onClick={cleanFiltersAndBack} aria-label={t('back', { defaultValue: 'Volver' })} sx={{ color: colors.primary[600] }}>
              <ArrowBack />
            </IconButton>
            <Box sx={{ flex: 1 }}>
              <Typography component="h1" variant="h4" fontWeight={700} color="text.primary">
                {t('pageTitle')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('subtitle')}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>

      <Container className="wp-body" sx={{ paddingY: `${spacing.xl}px` }}>
        <Stack spacing={`${`${spacing.xl}px`}px`}>
          {/* Tools Overview */}
          <Grid container spacing={`${`${spacing.lg}px`}px`}>
            {tools.map((tool, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Card
                  sx={{
                    height: '100%',
                    opacity: tool.available ? 1 : 0.6,
                    cursor: tool.available ? 'pointer' : 'not-allowed',
                    transition: 'all 0.3s',
                    '&:hover': tool.available
                      ? {
                          transform: 'translateY(-4px)',
                          boxShadow: 6,
                        }
                      : {},
                  }}
                >
                  <CardContent>
                    <Stack spacing={`${`${spacing.md}px`}px`} alignItems="center" textAlign="center">
                      <Box
                        sx={{
                          width: 80,
                          height: 80,
                          borderRadius: '50%',
                          backgroundColor: `${tool.color}15`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <tool.icon sx={{ fontSize: 40, color: tool.color }} />
                      </Box>
                      <Box>
                        <Typography variant="h6" fontWeight={typography.fontWeight.bold} gutterBottom>
                          {tool.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {tool.description}
                        </Typography>
                      </Box>
                      {!tool.available && (
                        <Typography
                          variant="caption"
                          sx={{
                            padding: `${spacing.xs} ${spacing.sm}`,
                            backgroundColor: 'action.hover',
                            borderRadius: `${spacing.borderRadius.sm}px`,
                            color: 'text.secondary',
                          }}
                        >
                          {t('comingSoon')}
                        </Typography>
                      )}
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Video Event Tracker */}
          <Card>
            <CardHeader
              title={t('videoEvents.title')}
              icon={<VideoLibrary />}
            />
            <CardContent>
              <VideoEventTracker />
            </CardContent>
          </Card>
        </Stack>
      </Container>
    </Box>
  );
};

export default HerramientasPageModern;
